import { CATEGORY_DEFAULT_OPTION, SortingOptions } from '../../util/consts'
import { SelectedFiltersType } from '../products/components/ProductFilter'
import { SEED_MIX_TYPE } from '../seed-orders/types/SeedOrderFormType'

/**
 * Converts an array of flattened category strings into a nested object structure.
 * @param categories - The array of flattened category strings.
 * @returns The nested object structure representing the categories.
 */ export const unflattenCategories = (categories: string[]): Record<string, string[]> => {
  return categories.reduce((acc: Record<string, string[]>, category: string) => {
    const [level1, level2] = category.split('>')
    if (!acc[level1]) {
      acc[level1] = []
    }
    if (level2) {
      acc[level1].push(level2)
    }
    return acc
  }, {})
}

const getSorting = (sortBy: string) => {
  switch (sortBy) {
    case SortingOptions.PriceAscending:
      return 'price:asc'
    case SortingOptions.PriceDescending:
      return 'price:desc'
    default:
      return '_text_match:desc'
  }
}

export const generateSearchRequests = (
  currentPage: number,
  selectedFilters: SelectedFiltersType,
  sortBy: string,
) => {
  const sorting = getSorting(sortBy)
  const priceQuery = getPriceQuery(selectedFilters)
  const { l1_categoryQuery, l2_categoryQuery } = getCategories(selectedFilters)
  const inStockQuery = getStockQuery(selectedFilters)
  const seedTypeQuery = getSeedTypeQuery(selectedFilters)

  const filter_by = [priceQuery, l1_categoryQuery, l2_categoryQuery, inStockQuery, seedTypeQuery]
    .filter((q) => !!q)
    .join(' && ')
  const searchQuery =
    selectedFilters.searchText.value === '' ? '*' : selectedFilters.searchText.value

  const searchRequests = {
    searches: [
      {
        preset: 'all_products_search',
        filter_by: filter_by,
        per_page: 25,
        page: currentPage,
        q: searchQuery,
        sort_by: sorting,
      },
    ],
  }

  return searchRequests
}

function getStockQuery(selectedFilters: SelectedFiltersType) {
  let inStockQuery = undefined

  if (selectedFilters.inStock.value) {
    if (selectedFilters.storeId.value === '*') {
      inStockQuery = undefined
    } else {
      inStockQuery = `available_stores:=[${selectedFilters.storeId.value}]`
    }
  }
  return inStockQuery
}

function getCategories(selectedFilters: SelectedFiltersType) {
  let l1_categoryQuery = undefined
  let l2_categoryQuery = undefined

  if (selectedFilters.category.value !== CATEGORY_DEFAULT_OPTION) {
    const split = selectedFilters.category.value.split('>')
    if (split[0]) l1_categoryQuery = `l1_category:=${split[0]}`
    if (split[1]) l2_categoryQuery = `l2_category:=${split[1]}`
  }
  return { l1_categoryQuery, l2_categoryQuery }
}

function getPriceQuery(selectedFilters: SelectedFiltersType) {
  if (
    selectedFilters.priceRange.value.from !== undefined &&
    selectedFilters.priceRange.value.to !== undefined
  ) {
    return `price:=[${selectedFilters.priceRange.value.from ?? '*'}..${selectedFilters.priceRange.value.to ?? '*'}]`
  } else if (selectedFilters.priceRange.value.from !== undefined) {
    return `price:>=${selectedFilters.priceRange.value.from}`
  } else if (selectedFilters.priceRange.value.to !== undefined) {
    return `price:<=${selectedFilters.priceRange.value.to}`
  }
  return undefined
}

export const filteredSeeds = [
  'Beet',
  'Brassica',
  'Cereal seed',
  'Clover',
  'Lucerne',
  'Pulse seed',
  'Ryegrass',
  'Sundry pasture',
  'Treatments',
]

export const CUSTOM_SEED_FILTERED_CATEGORIES = [
  'Beet',
  'Premix',
  'Seed maize',
  'Seed potatoes',
  'Treatments',
  'Vegetable seed',
]

function getSeedTypeQuery(selectedFilters: SelectedFiltersType) {
  if (selectedFilters.seedType?.value === SEED_MIX_TYPE.CUSTOM) {
    return `l1_category:=Seed && is_loose_product:true && l2_category:!=[${CUSTOM_SEED_FILTERED_CATEGORIES.join(', ')}]`
  }
  if (selectedFilters.seedType?.value === SEED_MIX_TYPE.PREMIX) {
    return `l1_category:=Seed`
  }
  // If no seed type, this is coming from general products and we want to restrict the categories
  return `l2_category:!=[${filteredSeeds.join(', ')}]`
}
